<template>
  <div
    class="topic-list"
    :class="{ 'is-dragover': isDragOver && enableDragDrop }"
    @dragover.prevent="enableDragDrop && handleDragOver()"
    @dragleave.prevent="enableDragDrop && handleDragLeave()"
    @drop.prevent="enableDragDrop && handleDrop($event)"
  >
    <!-- Header -->
    <div class="panel-heading">
      <div class="panel-heading-left">
        <span class="title is-6 mb-0">Topics</span>

        <b-tag
          v-if="topics"
          class="ml-1"
          type="is-info is-light"
          style="width: 1.5rem"
        >
          {{ filteredTopics.length }}
        </b-tag>

        <button-tooltip
          v-if="canEdit"
          tooltip="Create Topic"
          position="is-bottom"
          class="ml-1"
          type="is-primary"
          icon-left="plus"
          size="is-small"
          @click="$emit('create-topic')"
        />
      </div>

      <!-- Search and filter controls -->
      <div class="panel-heading-right">
        <b-input
          v-model="searchQuery"
          placeholder="Search topics..."
          size="is-small"
          icon="search"
          @input="filterTopics"
        />

        <b-dropdown
          v-model="selectedDifficulty"
          aria-role="list"
          position="is-bottom-left"
          :triggers="['hover']"
          :delay="300"
        >
          <template #trigger>
            <ButtonTooltip
              size="is-small"
              tooltip="Filter by difficulty level"
              position="is-top"
              :delay="300"
              type="is-light is-info"
              outlined
              :label="selectedDifficultyIcon"
            />
          </template>

          <b-dropdown-item value="" aria-role="listitem">
            <b-tag type="is-light" size="is-small" rounded>
              🌟 All Levels
            </b-tag>
          </b-dropdown-item>
          <b-dropdown-item
            v-for="level in difficultyLevels"
            :key="level"
            :value="level"
            aria-role="listitem"
          >
            <DifficultyBadge :difficulty="level" />
          </b-dropdown-item>
        </b-dropdown>

        <b-dropdown
          v-if="curriculums.length > 0"
          v-model="selectedCurriculumId"
          aria-role="list"
          position="is-bottom-left"
          :triggers="['hover']"
          :delay="300"
        >
          <template #trigger>
            <ButtonTooltip
              size="is-small"
              tooltip="Filter by curriculum"
              position="is-top"
              type="is-light is-info"
              outlined
              :delay="300"
              :label="selectedCurriculumIcon"
            />
          </template>

          <b-dropdown-item value="" aria-role="listitem">
            📚 All topics
          </b-dropdown-item>
          <b-dropdown-item
            v-for="curriculum in curriculums"
            :key="curriculum.id"
            :value="curriculum.id"
            aria-role="listitem"
          >
            📙 {{ curriculum.title }}
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>

    <!-- Topics List -->
    <div class="topics-container">
      <div v-if="filteredTopics.length === 0" class="p-4">
        <p v-if="searchQuery || selectedDifficulty">
          No topics match your filters
        </p>
        <p v-else>No topics available</p>
      </div>

      <div v-else class="topics-list">
        <TopicCard
          v-for="topic in sortedTopics"
          :key="topic.id"
          :topic="topic"
          :is-selected="topic.id === selectedTopicId"
          :usage="topicUsageMap.get(topic.id)"
          :canEdit="canEdit"
          :is-draggable="enableDragDrop"
          @click="$emit('select-topic', topic.id)"
          @preview="$emit('preview-topic', topic.id)"
          @edit="handleTopicEdit(topic)"
        >
          <template #extra>
            <b-tooltip
              v-if="getScenarioCount(topic.id)"
              :label="`${getScenarioCount(topic.id)} examples`"
              position="is-right"
              type="is-primary is-light"
            >
              <b-tag type="is-primary is-light" size="is-small">
                <b-icon icon="chess-board" size="is-small" class="mr-1" />
                {{ getScenarioCount(topic.id) }}
              </b-tag>
            </b-tooltip>
          </template>
        </TopicCard>
      </div>
    </div>

    <!-- Topic Editor Modal (if includeTopicEditor is true) -->
    <b-modal
      v-if="includeTopicEditor"
      v-model="isTopicEditorActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
    >
      <TopicEditor
        :topic="topicToEdit"
        @saved="handleTopicSaved"
        @close="closeTopicEditor"
      />
    </b-modal>
  </div>
</template>

<script>
import { TopicDifficulty } from "vue-chess-shared";
import TopicCard from "@/components/training/explorer/molecules/TopicCard.vue";
import ButtonTooltip from "@/components/ui/ButtonTooltip.vue";
import TopicEditor from "@/components/training/topics/TopicEditor.vue";
import DifficultyBadge from "@/components/training/explorer/atoms/DifficultyBadge.vue";

export default {
  name: "TopicList",

  components: {
    TopicCard,
    ButtonTooltip,
    TopicEditor,
    DifficultyBadge,
  },

  props: {
    topics: {
      type: Array,
      required: true,
    },
    selectedTopicId: {
      type: String,
      default: null,
    },
    scenarioCounts: {
      type: Map,
      default: () => new Map(),
    },
    topicUsageMap: {
      type: Map,
      default: () => new Map(),
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    enableDragDrop: {
      type: Boolean,
      default: false,
    },
    includeTopicEditor: {
      type: Boolean,
      default: false,
    },
    curriculums: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      searchQuery: "",
      selectedDifficulty: "",
      selectedCurriculumId: "",
      difficultyLevels: Object.values(TopicDifficulty),
      isDragOver: false,
      isTopicEditorActive: false,
      topicToEdit: null,
    };
  },

  computed: {
    filteredTopics() {
      return this.topics.filter((topic) => {
        // Search in title, tags, status, and difficulty level
        const searchLower = this.searchQuery.toLowerCase();
        const matchesSearch =
          !this.searchQuery ||
          [
            topic.title,
            topic.status,
            topic.difficultyLevel,
            ...(topic.tags || []),
            topic.isPublic ? "public" : "private",
          ].some(
            (field) =>
              field && field.toString().toLowerCase().includes(searchLower)
          );

        const matchesDifficulty =
          !this.selectedDifficulty ||
          topic.difficultyLevel === this.selectedDifficulty;

        const matchesCurriculum =
          !this.selectedCurriculumId ||
          this.getTopicCurriculumIds(topic.id).includes(
            this.selectedCurriculumId
          );

        return matchesSearch && matchesDifficulty && matchesCurriculum;
      });
    },

    sortedTopics() {
      return [...this.filteredTopics].sort((a, b) =>
        a.title.localeCompare(b.title)
      );
    },

    selectedDifficultyIcon() {
      if (!this.selectedDifficulty) {
        return "⭐"; // Star for All Levels
      }

      const icons = {
        [TopicDifficulty.Beginner]: "🐣",
        [TopicDifficulty.Rookie]: "🐤",
        [TopicDifficulty.Intermediate]: "🐥",
        [TopicDifficulty.Advanced]: "🦅",
      };

      return icons[this.selectedDifficulty] || "⭐";
    },

    selectedCurriculumIcon() {
      if (!this.selectedCurriculumId) {
        return "📚"; // Book for All Curriculums
      }

      return "📙"; // Book icon for selected curriculum
    },

    selectedCurriculum() {
      if (!this.selectedCurriculumId) return null;
      return (
        this.curriculums.find((c) => c.id === this.selectedCurriculumId) || null
      );
    },
  },

  methods: {
    getScenarioCount(topicId) {
      return this.scenarioCounts.get(topicId) || 0;
    },

    getTopicCurriculumIds(topicId) {
      // Return an array of curriculum IDs that contain this topic
      return this.curriculums
        .filter((curriculum) => {
          // Check if topic is in any module
          const inModules = curriculum.modules?.some((module) =>
            module.topicIds?.includes(topicId)
          );

          // Check if topic is in any lesson
          const inLessons = curriculum.lessons?.some(
            (lesson) => lesson.topicId === topicId
          );

          return inModules || inLessons;
        })
        .map((curriculum) => curriculum.id);
    },

    filterTopics() {
      if (
        this.selectedTopicId &&
        !this.filteredTopics.find((t) => t.id === this.selectedTopicId)
      ) {
        this.$emit("select-topic", null);
      }
    },

    handleTopicEdit(topic) {
      if (this.includeTopicEditor) {
        this.topicToEdit = topic;
        this.isTopicEditorActive = true;
      } else {
        this.$emit("edit-topic", topic);
      }
    },

    closeTopicEditor() {
      this.isTopicEditorActive = false;
      this.topicToEdit = null;
    },

    handleTopicSaved() {
      this.$emit("topics-updated");
      this.closeTopicEditor();
    },

    // Drag and drop methods
    handleDragOver() {
      this.isDragOver = true;
    },

    handleDragLeave() {
      this.isDragOver = false;
    },

    handleDrop(event) {
      this.isDragOver = false;
      this.$emit("drop", event);
    },
  },
};
</script>

<style scoped>
.topic-list {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
  border: 1px solid #dbdbdb;
}

.panel-heading {
  padding: 0.5rem;
  background-color: #f5f5f5;
  border-bottom: 2px solid #dbdbdb;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  text-align: left;
}

.panel-heading-left {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.panel-heading-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  min-width: 0; /* Allow container to shrink */
  flex: 1;
}

/* Make input shrink while maintaining minimum width */
.panel-heading-right :deep(.input) {
  min-width: 120px;
  width: 100%;
}

.topics-container {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
}

.topics-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Drag and drop styles */
.topic-list.is-dragover {
  background-color: rgba(0, 209, 178, 0.1);
  border: 1px dashed #00d1b2;
}
</style>
